import * as React from 'react';

import { FocusIndicator } from '~shared/components/FocusIndicator/FocusIndicator';
import { IconExpand } from '~shared/components/icons/standard';

import { ImageZoomPopup } from './ImageZoomPopup';

export interface ImageZoomProps {
  children: React.ReactNode;
  /** Path to full-size image. */
  zoomSrc: string;
  openLabel: string;
  closeLabel: string;
}

export function ImageZoom({
  children,
  zoomSrc,
  openLabel,
  closeLabel,
}: ImageZoomProps) {
  const [isZoomed, setIsZoomed] = React.useState(false);
  const [isPopupLoaded, setIsPopupLoaded] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const openZoom = React.useCallback(
    (e) => {
      if (!isZoomed) {
        e.preventDefault();
        setIsZoomed(true);
      }
    },
    [isZoomed]
  );

  const handlePopupLoaded = React.useCallback(() => setIsPopupLoaded(true), []);
  const handlePopupUnloaded = React.useCallback(() => {
    setIsZoomed(false);
    setIsPopupLoaded(false);
    buttonRef.current?.focus({ preventScroll: true });
  }, []);

  const isExpanded = isZoomed && isPopupLoaded;

  return (
    <div
      className={`relative inline-block transition-opacity ${
        isExpanded ? 'duration-200 opacity-0' : ''
      }`}
      ref={wrapperRef}
    >
      {children}
      <button
        className="group absolute inset-0 z-10 w-full h-full m-0 bg-transparent border-none outline-none appearance-none cursor-zoom-in"
        aria-label={openLabel}
        onClick={openZoom}
        ref={buttonRef}
      >
        <div className="absolute top-0 right-0 p-2 opacity-0 group-hocus:opacity-100 transition-opacity ease-in-out duration-150">
          <div className="absolute inset-0 bg-grey-50 opacity-75 rounded-bl-1" />
          <div className="relative flex text-blue-900">
            <IconExpand />
          </div>
        </div>
        <FocusIndicator />
      </button>

      {typeof window !== 'undefined' && isZoomed && (
        <ImageZoomPopup
          zoomSrc={zoomSrc}
          closeLabel={closeLabel}
          onLoad={handlePopupLoaded}
          onUnload={handlePopupUnloaded}
          parentRef={wrapperRef}
        />
      )}
    </div>
  );
}
